import { defaultSettings } from 'src/components/settings/config-setting';
import { VideoFilterType } from 'src/provider/VoiceChannel/livekit/types';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GroupState, TodoView, UIFeature, UIState } from './types';

const initialGroupState: GroupState = {
  viewType: TodoView.COLUMNS,
  columnNumber: 5,
  expandedSubList: true,
  activeSubList: 0,
  sublistHeight: 400,
  sublistColsWidth: {},
};

const useUIState = create(
  persist<UIState>(
    (set) => ({
      navBarHidden: {
        communicate: false,
        collaborate: false,
        ai: false,
      },
      navBarExpanded: Object.values(UIFeature).reduce(
        (acc, curr) => ({ ...acc, [curr]: true }),
        {},
      ) as Record<UIFeature, boolean>,
      driveViewType: 'grid',
      docsFullWidth: false,
      docScrollHeight: {},
      docsExpanded: {},
      toDoGroupStates: {},
      meetLastPopPosition: { x: 0, y: 0, height: 250, width: 350 },
      preferenceState: {},
      dismissLanguageModal: false,
      docsIDList: [],
      chatIDList: [],
      videoFilter: null,
      filterParams: {
        blurRadius: 5,
        blurDelegate: 'GPU',
        virtualBackground: '',
      },
      uploadedURLs: [],
      micMuted: false,
      addDocToList: (docId: string) => {
        set((state) => ({
          docsIDList: [...state.docsIDList, docId],
        }));
      },
      deleteDocFromList: (docId: string) => {
        set((state) => ({
          docsIDList: [...state.docsIDList.filter((item) => item !== docId)],
        }));
      },
      addChatToList: (chatId: string) => {
        set((state) => ({
          chatIDList: [...state.chatIDList, chatId],
        }));
      },
      deleteChatFromList: (chatId: string) => {
        set((state) => ({
          chatIDList: [...state.chatIDList.filter((item) => item !== chatId)],
        }));
      },
      reset() {
        set({
          navBarHidden: {
            communicate: false,
            collaborate: false,
            ai: true,
          },
          navBarExpanded: Object.values(UIFeature).reduce(
            (acc, curr) => ({ ...acc, [curr]: true }),
            {},
          ) as Record<UIFeature, boolean>,
          driveViewType: 'grid',
          docsFullWidth: false,
          docScrollHeight: {},
          docsExpanded: {},
          toDoGroupStates: {},
          meetLastPopPosition: { x: 0, y: 0, height: 250, width: 350 },
          preferenceState: {},
          dismissLanguageModal: false,
        });
      },
      setNavBarHidden: (area, hidden) =>
        set((state) => ({
          navBarHidden: { ...state.navBarHidden, [area]: hidden },
        })),
      setNavBarExpanded: (feature, expanded) =>
        set((state) => ({
          navBarExpanded: { ...state.navBarExpanded, [feature]: expanded },
        })),
      setDriveViewType: (viewType) => set({ driveViewType: viewType }),
      setDocsFullWidth: (expanded) => set({ docsFullWidth: expanded }),
      setDocScrollHeight: (docId, height) =>
        set((state) => ({
          docScrollHeight: {
            ...state.docScrollHeight,
            [docId]: height,
          },
        })),
      setDocExpanded: (docId, expanded) =>
        set((state) => ({
          docsExpanded: {
            ...state.docsExpanded,
            [docId]: expanded,
          },
        })),
      setToDoGroupState: (groupId, newState) =>
        set((currentState) => ({
          toDoGroupStates: {
            ...currentState.toDoGroupStates,
            [groupId]: {
              ...initialGroupState,
              ...currentState.toDoGroupStates[groupId],
              ...newState,
            },
          },
        })),
      setMeetLastPopPosition: (position) =>
        set({ meetLastPopPosition: position }),
      setPreferenceState: (accountIndex, newState) =>
        set((currentState) => ({
          preferenceState: {
            ...currentState.preferenceState,
            [accountIndex]: {
              ...defaultSettings,
              ...currentState.preferenceState[accountIndex],
              ...newState,
            },
          },
        })),
      setDismissLanguageModal: (dismissLanguageModal) =>
        set({ dismissLanguageModal }),
      setVideoFilter: (videoFilter: VideoFilterType) => set({ videoFilter }),
      setFilterParams: (filterParam) =>
        set((currentState) => ({
          filterParams: {
            ...currentState.filterParams,
            ...filterParam,
          },
        })),
      setUploadedURLs: (uploadedURLs) => set({ uploadedURLs }),
      setMicMuted: (micMuted: boolean) => set({ micMuted }),
    }),
    {
      name: 'ui-state-storage',
      getStorage: () => localStorage,
    },
  ),
);

export default useUIState;
